/**
 * Normalises every way the API can fail into one shape the UI switches on:
 *  - Better Auth client results  -> mapAuthError({ status, code, message })
 *  - hc / fetch Responses        -> mapResponseError(res)
 *  - thrown errors (offline etc) -> mapUnknownError(err)
 * Server messages are kept when present; `kind` is what screens should branch on.
 */
export type ApiErrorKind =
  | 'validation' //            422 - fieldErrors maps onto form fields
  | 'duplicate_email' //       signup with an existing email
  | 'invalid_credentials' //   wrong email / password
  | 'email_not_verified' //    403 EMAIL_NOT_VERIFIED - send an OTP and go to verify
  | 'invalid_code' //          wrong / expired OTP, TOTP or backup code
  | 'invalid_token' //         expired / invalid reset or invite link
  | 'too_many_attempts' //     request a new code
  | 'account_locked' //        423 - password lockout
  | 'two_factor_locked' //     429 ACCOUNT_TEMPORARILY_LOCKED - 2FA lockout
  | 'two_factor_expired' //    the pending 2FA challenge cookie is gone; sign in again
  | 'rate_limited' //          429 - show cooldown (retryAfterSeconds)
  | 'suspended' //             403 account suspended
  | 'unauthorized' //          401 no session
  | 'forbidden' //             403 wrong role / not your resource
  | 'not_found'
  | 'network'
  | 'server'
  | 'unknown';

export interface FieldError {
  /** Dotted path, usable directly as a React Hook Form field name. */
  field: string;
  message: string;
}

export interface MappedApiError {
  kind: ApiErrorKind;
  status: number | undefined;
  code: string | undefined;
  message: string;
  fieldErrors: FieldError[];
  /** Present for rate_limited / lockouts when the server told us how long. */
  retryAfterSeconds: number | undefined;
}

const KIND_BY_AUTH_CODE: Record<string, ApiErrorKind> = {
  USER_ALREADY_EXISTS: 'duplicate_email',
  USER_ALREADY_EXISTS_USE_ANOTHER_EMAIL: 'duplicate_email',
  INVALID_EMAIL_OR_PASSWORD: 'invalid_credentials',
  EMAIL_NOT_VERIFIED: 'email_not_verified',
  INVALID_OTP: 'invalid_code',
  OTP_EXPIRED: 'invalid_code',
  OTP_HAS_EXPIRED: 'invalid_code',
  INVALID_CODE: 'invalid_code',
  INVALID_BACKUP_CODE: 'invalid_code',
  INVALID_TOKEN: 'invalid_token',
  TOKEN_EXPIRED: 'invalid_token',
  TOO_MANY_ATTEMPTS: 'too_many_attempts',
  TOO_MANY_ATTEMPTS_REQUEST_NEW_CODE: 'too_many_attempts',
  ACCOUNT_TEMPORARILY_LOCKED: 'two_factor_locked',
  INVALID_TWO_FACTOR_COOKIE: 'two_factor_expired',
  PASSWORD_TOO_SHORT: 'validation',
  PASSWORD_TOO_LONG: 'validation',
};

const FIELD_BY_AUTH_CODE: Record<string, string> = {
  PASSWORD_TOO_SHORT: 'password',
  PASSWORD_TOO_LONG: 'password',
  USER_ALREADY_EXISTS: 'email',
  USER_ALREADY_EXISTS_USE_ANOTHER_EMAIL: 'email',
};

const DEFAULT_MESSAGE: Record<ApiErrorKind, string> = {
  validation: 'Please check the highlighted fields.',
  duplicate_email: 'An account with this email already exists.',
  invalid_credentials: 'Wrong email or password.',
  email_not_verified: 'Please verify your email first.',
  invalid_code: 'That code is invalid or has expired.',
  invalid_token: 'This link is invalid or has expired.',
  too_many_attempts: 'Too many attempts. Please request a new code.',
  account_locked: 'Account temporarily locked. Try again later.',
  two_factor_locked: 'Too many failed attempts. Try again later.',
  two_factor_expired: 'Your sign-in session expired. Please sign in again.',
  rate_limited: 'Too many requests. Please wait a moment.',
  suspended: 'Account suspended. Contact support.',
  unauthorized: 'Please sign in to continue.',
  forbidden: "You don't have access to this.",
  not_found: 'Not found.',
  network: "Can't reach the server. Check your connection.",
  server: 'Something went wrong on our side. Please try again.',
  unknown: 'Something went wrong. Please try again.',
};

interface RawError {
  status?: number;
  code?: string;
  /** Our own envelope: `{ error: 'Forbidden', message }`. */
  error?: string;
  message?: string;
  issues?: { path?: (string | number)[]; message?: string }[];
  /** ISO time a lockout ends (423 from sign-in). */
  lockedUntil?: string;
}

function kindFromStatus(status: number | undefined, raw: RawError): ApiErrorKind {
  if (status === undefined) return 'unknown';
  if (status === 401) return 'unauthorized';
  if (status === 403) return /suspended/i.test(raw.message ?? '') ? 'suspended' : 'forbidden';
  if (status === 404) return 'not_found';
  if (status === 409) return 'duplicate_email';
  if (status === 422) return 'validation';
  if (status === 423) return 'account_locked';
  if (status === 429) return 'rate_limited';
  if (status >= 500) return 'server';
  return 'unknown';
}

function secondsUntil(iso: string | undefined, now: number): number | undefined {
  const at = iso ? Date.parse(iso) : NaN;
  return Number.isFinite(at) ? Math.max(0, Math.ceil((at - now) / 1000)) : undefined;
}

function build(raw: RawError, retryAfterSeconds?: number, now = Date.now()): MappedApiError {
  const kind = (raw.code && KIND_BY_AUTH_CODE[raw.code]) || kindFromStatus(raw.status, raw);

  const fieldErrors: FieldError[] =
    raw.issues?.map((i) => ({ field: (i.path ?? []).join('.'), message: i.message ?? DEFAULT_MESSAGE.validation })) ??
    [];
  const codeField = raw.code ? FIELD_BY_AUTH_CODE[raw.code] : undefined;
  if (codeField && fieldErrors.length === 0) {
    fieldErrors.push({ field: codeField, message: raw.message || DEFAULT_MESSAGE[kind] });
  }

  return {
    kind,
    status: raw.status,
    code: raw.code,
    message: raw.message || DEFAULT_MESSAGE[kind],
    fieldErrors,
    retryAfterSeconds: retryAfterSeconds ?? secondsUntil(raw.lockedUntil, now),
  };
}

/** For the `error` member of a Better Auth client result (`{ data, error }`). */
export function mapAuthError(
  error: { status?: number; code?: string; message?: string; lockedUntil?: string } | null | undefined,
  now = Date.now(),
): MappedApiError | null {
  if (!error) return null;
  return build(
    { status: error.status, code: error.code, message: error.message, lockedUntil: error.lockedUntil },
    undefined,
    now,
  );
}

/** The API sends the reset instant (epoch ms) in X-RateLimit-Reset; Retry-After (seconds) wins if present. */
function retryAfter(res: Response, now: number): number | undefined {
  const header = res.headers.get('retry-after');
  if (header && Number.isFinite(Number(header))) return Math.max(0, Math.ceil(Number(header)));
  const reset = Number(res.headers.get('x-ratelimit-reset'));
  if (res.status === 429 && Number.isFinite(reset) && reset > 0) {
    return Math.max(0, Math.ceil((reset - now) / 1000));
  }
  return undefined;
}

/** For a non-ok `Response` from the hc client or fetch. Never throws on a non-JSON body. */
export async function mapResponseError(res: Response, now = Date.now()): Promise<MappedApiError> {
  let body: RawError = {};
  try {
    body = (await res.clone().json()) as RawError;
  } catch {
    // non-JSON / empty body: fall back to the status alone
  }
  return build({ ...body, status: res.status }, retryAfter(res, now), now);
}

/** For anything thrown. fetch() rejects with a TypeError when offline / blocked, which is a network failure. */
export function mapUnknownError(err: unknown): MappedApiError {
  if (err instanceof TypeError) {
    return {
      kind: 'network',
      status: undefined,
      code: undefined,
      message: DEFAULT_MESSAGE.network,
      fieldErrors: [],
      retryAfterSeconds: undefined,
    };
  }
  return build({ message: err instanceof Error ? err.message : undefined });
}
